import React, { useMemo } from "react"

import DiffMatchPatch from "diff-match-patch"

import { cn } from "@/utils/cn"

export const DIFF_MODE = {
  WORD: "word",
  CHAR: "char",
} as const

type DiffMode = (typeof DIFF_MODE)[keyof typeof DIFF_MODE]

interface InlineDiffProps {
  oldText: string
  newText: string
  className?: string
  mode?: DiffMode
}

export const InlineDiff: React.FC<InlineDiffProps> = ({
  oldText,
  newText,
  className,
  mode = DIFF_MODE.WORD,
}) => {
  const diffs = useMemo(() => {
    const dmp = new DiffMatchPatch()
    if (mode === DIFF_MODE.CHAR) {
      const result = dmp.diff_main(oldText || "", newText || "")
      dmp.diff_cleanupSemantic(result)
      return result
    }
    const wordA = (oldText || "").replace(/\S+/g, "$&\n")
    const wordB = (newText || "").replace(/\S+/g, "$&\n")
    const { chars1, chars2, lineArray } = dmp.diff_linesToChars_(wordA, wordB)
    const result = dmp.diff_main(chars1, chars2, false)
    dmp.diff_charsToLines_(result, lineArray)
    return result.map(
      ([op, text]) => [op, text.replace(/\n/g, "")] as [number, string],
    )
  }, [oldText, newText, mode])

  return (
    <div className={cn("text-sm whitespace-pre-wrap break-words", className)}>
      {diffs.map(([type, text], index) => (
        <span
          key={index}
          className={cn(
            type === 0 && "text-coval-text",
            type === -1 &&
              "bg-coval-danger-surface text-coval-danger-on-surface line-through decoration-2 px-0.5 rounded",
            type === 1 &&
              "bg-coval-success-surface text-coval-success-on-surface px-0.5 rounded",
          )}
        >
          {text}
        </span>
      ))}
    </div>
  )
}
